import React, { useContext, useState, useEffect } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import * as Yup from "yup";

import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import AppContext from "../../context/AppContext";
import { AddToCart } from "../../modals/Cart";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import { MODULES } from "../../utils/routesNames";
import { Link } from "react-router-dom";
import {
  AddToCartService,
  ViewCartService,
  UpdateCartService,
  AddSavedProductService,
} from "../../services/services";
import {
  addToCartMessages as MSG,
  generalMessages,
} from "../../utils/constants";
import { INCREMENT, DECREMENT } from "../../assets/images/svgImage";
import { toast } from "react-toastify";

const addToCartSchema = Yup.object({
  Quantity: Yup.number()
    .min(MSG.MIN_QTY, MSG.QTY_REQUIRED)
    .positive(MSG.INVALID_QTY)
    .integer(MSG.INVALID_QTY)
    .required(MSG.QTY_REQUIRED),
});

interface AddToCartInterface {
  id: string | number;
  is_saved: number;
  isListing?: boolean;
  out_of_stock?: boolean;
  units_per_carton?: number | null;
}

const ListingWidgetAllProducts: React.FC<AddToCartInterface> = ({
  id,
  is_saved,
  isListing = false,
  out_of_stock = false,
  units_per_carton,
}) => {
  const { auth, cart, setCart, tokenExpired } = useContext(AppContext);

  const [quantityState, setQuantityState] = useState<AddToCart>({
    UserID: auth?.id,
    ProductID: id,
    Quantity: 1,
    OrderType: 'unit'
  });

  const [cartId, setCartId] = useState<number | null>(null);

  useEffect(() => {
    const cartItem = cart.find((product) => product.product_id === id);
    if (auth && cartItem?.quantity) {
      setCartId(cartItem.id);
      setQuantityState({ ...quantityState, Quantity: cartItem.quantity });
    }
  }, [cart, auth]); // eslint-disable-line

  const [loading, setLoading] = useState(false);

  const addProductToCart = (values: AddToCart, bag: any) => {
    // Save scroll position before cart update
    const scrollPosition = window.pageYOffset;

    let totalQuantity = values.OrderType === 'carton' ? values.Quantity * (units_per_carton || 1) : values.Quantity;
    let result: Promise<any>;
    if (cartId) {
      result = UpdateCartService(cartId, values.ProductID, totalQuantity);
    } else {
      values.Quantity = totalQuantity;
      result = AddToCartService(values);
    }
    result
      .then((result) => {
        if (result.data.flag) {
          toast.success(result.data.message);
        } else {
          toast.error(result.data.message || generalMessages.ERROR_MSG);
        }
        bag.setSubmitting(false);
        return ViewCartService();
      })
      .then((result) => {
        if (result.data.flag) {
          setCart(result.data.data);

          // Restore scroll position after state updates
          requestAnimationFrame(() => {
            window.scrollTo(0, scrollPosition);
          });
        }
      })
      .catch((err) => {
        bag.setSubmitting(false);
        if (err?.response?.status === 401) {
          tokenExpired();
        } else if (err?.response?.data?.message) {
          toast.error(err.response.data.message);
        } else {
          toast.error(generalMessages.ERROR_MSG);
        }
      });
  };

  const addToSavedList = (values: AddToCart) => {
    setLoading(true);
    AddSavedProductService(values.ProductID, "commonly")
      .then((result) => {
        if (result.data.flag) {
          setLoading(false);
          toast.success(result.data.message);
        } else {
          toast.error(result.data.message);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (err.response.status === 401) {
          tokenExpired();
        }
      });
  };

  return (
    <div className="action-bar">
      {auth && (
        <Formik
          enableReinitialize
          validateOnChange
          validateOnBlur
          validationSchema={addToCartSchema}
          initialValues={quantityState}
          onSubmit={(values, bag) => addProductToCart(values, bag)}
        >
          {({ isSubmitting, handleSubmit, setFieldValue, values }) => (
            <FormikForm
              className="action-bar d-flex flex-column justify-content-start align-items-center justify-content-sm-center mt-4"
              noValidate
              onSubmit={handleSubmit}
            >
                <div 
                  className="toggle-div d-flex mb-4" 
                  style={{ visibility: units_per_carton ? 'visible' : 'hidden' }}
                >
                  <Button
                    type="button"
                    variant={values.OrderType === 'unit' ? 'order-type' : 'outline-dark'}
                    className="rounded-left"
                    onClick={() => {
                      if (units_per_carton) {
                        setFieldValue('OrderType', 'unit');
                        setFieldValue('Quantity', 1);
                      }
                    }}
                  >
                    Unit
                  </Button>
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Tooltip id="carton-tooltip">
                        {units_per_carton} units per carton
                      </Tooltip>
                    }
                  >
                    <Button
                      type="button"
                      variant={values.OrderType === 'carton' ? 'order-type' : 'outline-dark'}
                      className="rounded-right"
                      onClick={() => {
                        if (units_per_carton) {
                          setFieldValue('OrderType', 'carton');
                          setFieldValue('Quantity', 1);
                        }
                      }}
                    >
                      Carton
                    </Button>
                  </OverlayTrigger>
                </div>

              <div className="d-flex justify-content-between w-100">
                <div className="number-input">
                  <Form.Group className="form-group">
                    <Field
                      type="number"
                      min="1"
                      name="Quantity"
                      className="form-control"
                      placeholder="Qty"
                      disabled={out_of_stock}
                      style={{ width: "100px" }}
                    />
                    <span className="invalid-control">
                      <ErrorMessage name="Quantity" />
                    </span>
                    <div className="action">
                      <Button
                        onClick={() => {
                          setFieldValue("Quantity", values.Quantity + 1, true);
                        }}
                        className="btn btn-icon"
                      >
                        <img src={INCREMENT} alt="^" />
                      </Button>
                      <Button
                        onClick={() => {
                          if (values.Quantity - 1 >= MSG.MIN_QTY)
                            setFieldValue(
                              "Quantity",
                              values.Quantity - 1,
                              true
                            );
                        }}
                        className="btn btn-icon"
                      >
                        <img src={DECREMENT} alt="v" />
                      </Button>
                    </div>
                  </Form.Group>
                </div>

              <div className={`text-md-right ${isListing ? "listing-cart" : ""}`}>
                <Button
                  type="submit"
                  name="addToCart"
                  variant="primary"
                  disabled={isSubmitting || out_of_stock}
                  className="mt-0" 
                >
                  {isSubmitting ? (
                    <Spinner animation="border" size="sm" variant="light" />
                  ) : cartId ? (
                    "Update Cart"
                  ) : (
                    "Add to cart"
                  )}
                </Button>
              </div>
              </div>
            </FormikForm>
          )}
        </Formik>
      )}

      {/* not logged in user */}
      {!auth && (
        <div className={isListing ? "listing-cart" : ""}>
          <Button as={Link} to={MODULES.login.url} variant="primary">
            Add to Cart
          </Button>
        </div>
      )}
    </div>
  );
};

export default ListingWidgetAllProducts;
